import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { Spinner } from "@portal/components/primitives";
import type { ReactNode } from "react";
import { isApiError } from "../../../../../../utils/error";
import { AdminAssistantChartNotice } from "./AdminAssistantChartNotice";

/**
 * The states every chart stored under its conversation goes through, so a drawn image and a
 * tool's structure fail the same way. A 404 is a chart that was never stored, which stays missing,
 * so it is said plainly rather than offered a retry or reported.
 */
export const AdminAssistantStoredChart = <T,>({
	query,
	resource,
	children,
}: {
	query: {
		data: T | undefined;
		error: Error | null;
		isFetching: boolean;
		refetch: () => unknown;
	};
	/** What failed to load, in the underwriter's words: "chart", "org chart". */
	resource: string;
	children: (data: T) => ReactNode;
}) => {
	if (isApiError(query.error) && query.error.statusCode === 404) {
		return (
			<AdminAssistantChartNotice
				danger
			>{`This ${resource} is not available.`}</AdminAssistantChartNotice>
		);
	}
	if (query.error !== null) {
		return (
			<ReportedRetryableLoadError
				resource={resource}
				error={query.error}
				onRetry={query.refetch}
				retrying={query.isFetching}
			/>
		);
	}
	if (query.data === undefined) return <Spinner />;
	return <>{children(query.data)}</>;
};
